import GridViewIcon from "@mui/icons-material/GridView"
import SearchIcon from "@mui/icons-material/Search"
import TocIcon from "@mui/icons-material/Toc"
import {
	Button,
	Divider,
	Grid,
	IconButton,
	InputBase,
	Paper,
} from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { AxiosPromise } from "axios"
import {
	ChangeEvent,
	FormEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useState,
} from "react"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css" // theme css file
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useDispatch, useSelector } from "react-redux"

import { useNavigate } from "react-router"
import BanksList from "../../components/banks/list"
import BankEnhancedTable from "../../components/banks/table"
import SpinnerCard from "../../components/file/spinner/card"
import Sort from "../../components/sort"
import { AppDispatch } from "../../store"
import { fetchBanks, getBanksPaginated } from "../../store/actions/bank"
import { resetDefault } from "../../store/files-list/files-slice"
import { RootState } from "../../store/reducers"

export interface IBanksProperties {}

export interface IDatePicker {
	startDate: undefined | Date
	endDate: undefined | Date
	key: string
}

const BanksListView: FunctionComponent<IBanksProperties> = () => {
	const theme = useTheme()
	const { t } = useTranslation()
	const navigate = useNavigate()
	const dispatch = useDispatch<AppDispatch>()
	const authState = useSelector((state: RootState) => state.auth)
	const banksLoadingState: boolean = useSelector(
		(state: RootState) => state.banksPaginated.loading
	)
	const fileFetchLoadingState = useSelector(
		(state: RootState) => state.banksPaginated.loadingFetch
	)
	const filenextUrlState = useSelector(
		(state: RootState) => state.banksPaginated?.list?.nextUrl
	)
	const permissions = useSelector(
		(state: RootState) => state.auth?.user?.permissions
	)

	const hasCrudBankEnabled: boolean = permissions
		? permissions.some(
				(perm) => perm.permissionName === "CRUD_BANK" && perm.enabled === true
			)
		: false

	const [sortCriteria, setSortCriteria] = useState([
		{ field: "id", order: "asc" },
	])
	const [filterName, setFilterName] = useState<undefined | string>(undefined)
	const [whatsappFilter, setWhatsappFilter] = useState<boolean | undefined>(
		undefined
	)
	const [autoAcceptFilter, setAutoAcceptFilter] = useState<boolean | undefined>(
		undefined
	)
	const [checkIbanFilter, setCheckIbanFilter] = useState<boolean | undefined>(
		undefined
	)
	const [viewType, setViewType] = useState<undefined | string>("grid")

	const ButtonViewType = styled(Button)(({ theme }) => ({
		"&:hover": {
			backgroundColor: theme.palette.secondary.contrastText,
			color: theme.palette.secondary.main,
		},
	}))

	const { ref, inView } = useInView({
		threshold: 1,
		triggerOnce: false,
	})

	const setFilters: { [key: string]: any } = useCallback(() => {
		const filter: { [key: string]: any } = {}

		if (filterName) filter.bankName = filterName
		if (typeof autoAcceptFilter === "boolean")
			filter.autoAccept = autoAcceptFilter

		if (typeof whatsappFilter === "boolean") filter.whatsapp = whatsappFilter

		if (typeof checkIbanFilter === "boolean") filter.checkIban = checkIbanFilter

		return filter
	}, [filterName, checkIbanFilter, autoAcceptFilter, whatsappFilter])

	const handleSearch = () => {
		const filters: { [key: string]: any } = setFilters()
		const userSort = sortCriteria
			.map((criteria) => `${criteria.field}:${criteria.order}`)
			.join(",")
		dispatch(
			getBanksPaginated({
				page: 0,
				pageSize: 10,
				filter: filters,
				sort: userSort,
			})
		)
			.then((response: AxiosPromise) => {})
			.catch(() => {})
	}

	useEffect(() => {
		if (authState?.isAuthenticate) {
			handleSearch()
		}
	}, [authState?.isAuthenticate])

	useEffect(() => {
		resetDefault()
	}, [])

	useEffect(() => {
		!hasCrudBankEnabled && navigate("/")
	}, [hasCrudBankEnabled])

	useEffect(() => {
		if (inView && filenextUrlState && !fileFetchLoadingState) {
			dispatch(fetchBanks({ url: filenextUrlState }))
				.then((response: AxiosPromise) => {})
				.catch(() => {})
		}
	}, [
		inView,
		filenextUrlState,
		fileFetchLoadingState,
		authState?.isAuthenticate,
	])

	return (
		<>
			<Paper
				onSubmit={(event: FormEvent) => {
					event.stopPropagation()
					event.preventDefault()
					handleSearch()
				}}
				component="form"
				sx={{
					p: "2px 4px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<InputBase
					value={filterName}
					onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
						setFilterName(event.target.value)
					}
					sx={{ ml: 1, flex: 1 }}
					placeholder={t("search.placeholder", { ns: "bank" })}
					inputProps={{
						"aria-label": t("search.accessibility.label", { ns: "bank" }),
					}}
				/>
				<IconButton type="submit" sx={{ p: "10px" }} aria-label="search">
					<SearchIcon />
				</IconButton>
				<Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
				<Grid container gap="16px" sx={{ width: "fit-content" }}>
					{/* TODO new release
					<FiltersBank
						autoAcceptFilter={autoAcceptFilter}
						setAutoAcceptFilter={setAutoAcceptFilter}
						checkIbanFilter={checkIbanFilter}
						setCheckIbanFilter={setCheckIbanFilter}
						handleSearch={handleSearch}
						whatsappFilter={whatsappFilter}
						setWhatsappFilter={setWhatsappFilter}
					/>*/}
					<Sort
						handleSearch={handleSearch}
						sortCriteria={sortCriteria}
						setSortCriteria={setSortCriteria}
						sortingOptions={[
							"id",
							"creationDate",
							"bankName",
							"enabled",
							"whatsapp",
						]}
						availableOptionsInitial={[
							"creationDate",
							"bankName",
							"enabled",
							"whatsapp",
						]}
					/>
				</Grid>
			</Paper>

			<Grid
				container
				alignItems={"center"}
				justifyContent={"flex-end"}
				width={"100%"}
				sx={{ marginTop: "16px" }}
			>
				<ButtonViewType
					variant={viewType === "grid" ? "contained" : "outlined"}
					onClick={(e) => setViewType("grid")}
					sx={{
						backgroundColor:
							viewType === "grid"
								? theme.palette.secondary.main
								: theme.palette.primary.main,
					}}
					startIcon={
						<GridViewIcon
							style={{
								backgroundColor:
									viewType === "grid"
										? theme.palette.secondary.main
										: theme.palette.primary.main,
								color:
									viewType === "grid"
										? theme.palette.secondary.contrastText
										: theme.palette.primary.contrastText,
							}}
						/>
					}
				></ButtonViewType>
				<ButtonViewType
					variant={viewType === "table" ? "contained" : "outlined"}
					onClick={(e) => setViewType("table")}
					sx={{
						marginLeft: "16px",
						backgroundColor:
							viewType === "table"
								? theme.palette.secondary.main
								: theme.palette.primary.main,
					}}
					startIcon={
						<TocIcon
							style={{
								backgroundColor:
									viewType === "table"
										? theme.palette.secondary.main
										: theme.palette.primary.main,
								color:
									viewType === "table"
										? theme.palette.secondary.contrastText
										: theme.palette.primary.contrastText,
							}}
						/>
					}
				></ButtonViewType>
			</Grid>
			{banksLoadingState ? (
				<SpinnerCard />
			) : (
				<>
					{viewType === "grid" ? (
						<BanksList />
					) : (
						<BankEnhancedTable
							sortCriteria={sortCriteria}
							setSortCriteria={setSortCriteria}
						/>
					)}
					{fileFetchLoadingState ? (
						<SpinnerCard />
					) : (
						<div ref={ref} style={{ height: "30px" }}></div>
					)}
				</>
			)}
		</>
	)
}

export default BanksListView
